import { Fragment, useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion, useReducedMotion, useScroll, useTransform } from "framer-motion";
import { ArrowLeft, ArrowUpRight } from "lucide-react";
import { api, articleImageUrl } from "@/lib/api";
import usePageMeta from "@/hooks/usePageMeta";

const EASE = [0.16, 1, 0.3, 1];

function MaskedTitle({ text }) {
  const words = text.split(" ").filter(Boolean);
  return (
    <h1 data-testid="article-title" className="mt-6 max-w-4xl font-serif text-4xl font-normal leading-[1.08] tracking-[-0.03em] sm:text-5xl lg:text-6xl">
      {words.map((word, index) => (
        <Fragment key={`${word}-${index}`}>
          <span className="inline-block overflow-hidden pb-[0.16em] -mb-[0.16em] align-bottom">
            <motion.span
              className="inline-block will-change-transform"
              initial={{ y: "115%" }}
              animate={{ y: 0 }}
              transition={{ duration: 0.85, delay: 0.3 + index * 0.055, ease: EASE }}
            >
              {word}
            </motion.span>
          </span>
          {index < words.length - 1 ? " " : null}
        </Fragment>
      ))}
    </h1>
  );
}

function EditorialMarquee({ category }) {
  const phrase = `UGI INSIGHTS — ${category.toUpperCase()} — EDITORIAL PERSPECTIVES — INDIA · GCC · AFRICA · GLOBAL — `;
  return (
    <div data-testid="article-marquee" aria-hidden="true" className="editorial-marquee overflow-hidden border-b border-[#E5DFD3] bg-[#F5F2EB] py-5">
      <div className="editorial-marquee-track">
        {[0, 1].map((copy) => (
          <span key={copy} className="editorial-marquee-segment">{phrase.repeat(3)}</span>
        ))}
      </div>
    </div>
  );
}

function ArticleExperience({ article, onEnquiry }) {
  const heroRef = useRef(null);
  const imageRef = useRef(null);
  const reduceMotion = useReducedMotion();
  const { scrollYProgress } = useScroll();
  const { scrollYProgress: heroProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const { scrollYProgress: imageProgress } = useScroll({ target: imageRef, offset: ["start end", "end start"] });
  const heroImageY = useTransform(heroProgress, [0, 1], ["0%", "22%"]);
  const featuredImageY = useTransform(imageProgress, [0, 1], ["-7%", "7%"]);

  const publishedDate = new Date(`${article.publication_date}T00:00:00`).toLocaleDateString("en", { year: "numeric", month: "long", day: "numeric" });
  const featuredUrl = articleImageUrl(article.featured_image_path);

  return (
    <main data-testid="article-page">
      <motion.div data-testid="article-reading-progress" className="fixed inset-x-0 top-0 z-[60] h-[3px] origin-left bg-[#C5A059]" style={{ scaleX: scrollYProgress }} />

      <section ref={heroRef} data-testid="article-page-hero" className="relative overflow-hidden bg-[#1C1613] text-[#FDFBF7]">
        <motion.img
          src={featuredUrl}
          alt=""
          aria-hidden="true"
          style={reduceMotion ? {} : { y: heroImageY }}
          className="absolute inset-0 h-[118%] w-full object-cover opacity-40 sepia-[0.22]"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#1C1613]/95 via-[#1C1613]/75 to-[#1C1613]/35" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#1C1613] via-transparent to-[#1C1613]/40" />
        <div className="relative mx-auto flex min-h-[540px] max-w-7xl flex-col justify-center px-5 py-24 sm:px-8">
          <motion.p
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: EASE }}
            className="text-sm font-bold uppercase tracking-[0.3em] text-[#C5A059]"
          >
            {article.category} · {publishedDate} · {article.reading_time || 1} MIN READ
          </motion.p>
          <MaskedTitle text={article.title} />
          <motion.p
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.55, ease: EASE }}
            className="mt-7 max-w-2xl text-base font-light leading-8 text-[#E6DED2] sm:text-lg"
          >
            {article.summary}
          </motion.p>
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.8, delay: 0.85 }}>
            <Link data-testid="article-back-link" to="/insights" className="mt-9 inline-flex w-max items-center gap-3 border border-[#FDFBF7]/35 px-6 py-4 text-xs font-bold tracking-[0.16em] text-[#FDFBF7] transition-colors hover:border-[#C5A059] hover:text-[#C5A059]">
              <ArrowLeft size={16} strokeWidth={1.5} /> BACK TO INSIGHTS
            </Link>
          </motion.div>
        </div>
      </section>

      <EditorialMarquee category={article.category} />

      <article className="mx-auto max-w-4xl px-5 py-20 sm:px-8">
        <motion.div
          ref={imageRef}
          initial={{ opacity: 0, y: 32 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.9, ease: EASE }}
          className="article-spotlight relative overflow-hidden border border-[#E5DFD3]"
        >
          <motion.img
            data-testid="article-featured-image"
            src={featuredUrl}
            alt={article.title}
            style={reduceMotion ? { scale: 1.12 } : { y: featuredImageY, scale: 1.12 }}
            className="aspect-[16/9] w-full object-cover"
          />
        </motion.div>

        {article.tags?.length > 0 && (
          <motion.div
            data-testid="article-tags"
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-40px" }}
            transition={{ duration: 0.7, ease: EASE }}
            className="mt-8 flex flex-wrap gap-2"
          >
            {article.tags.map((tag) => <span key={tag} className="border border-[#C5A059]/30 px-3 py-1 text-[10px] font-bold uppercase tracking-[0.16em] text-[#8E6A2F]">{tag}</span>)}
          </motion.div>
        )}

        <motion.div
          initial={{ opacity: 0, y: 28 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.85, ease: EASE }}
        >
          <div data-testid="article-body" className="article-rich-text article-body mt-12" dangerouslySetInnerHTML={{ __html: article.body }} />
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.8, ease: EASE }}
          className="mt-16 border-y border-[#C5A059]/30 py-10"
        >
          <p className="text-sm font-bold uppercase tracking-[0.28em] text-[#C5A059]">Trade With UGI</p>
          <h2 className="mt-4 font-serif text-3xl font-normal text-[#1C1613]">Have a sourcing requirement to discuss?</h2>
          <button data-testid="article-enquiry-cta" onClick={onEnquiry} className="mt-7 inline-flex items-center gap-3 bg-[#1C1613] px-7 py-4 text-xs font-bold tracking-[0.16em] text-[#FDFBF7] transition-colors hover:bg-[#C5A059] hover:text-[#1C1613]">
            START AN ENQUIRY <ArrowUpRight size={16} strokeWidth={1.5} />
          </button>
        </motion.div>
      </article>
    </main>
  );
}

export default function ArticlePage({ onEnquiry }) {
  const { slug } = useParams();
  const [article, setArticle] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setArticle(null);
    setNotFound(false);
    api.get(`/articles/${slug}`).then((response) => setArticle(response.data)).catch(() => setNotFound(true));
  }, [slug]);

  usePageMeta(article ? {
    title: `${article.seo_title || article.title} | UGI Insights`,
    description: article.seo_description || article.summary,
    canonicalUrl: article.canonical_url || `${window.location.origin}/insights/${article.slug}`,
    ogTitle: article.seo_title || article.title,
    ogDescription: article.seo_description || article.summary,
    ogImage: articleImageUrl(article.og_image_path || article.featured_image_path),
    ogUrl: article.canonical_url || `${window.location.origin}/insights/${article.slug}`,
    type: "article",
    publishedTime: article.publication_date,
  } : {});

  if (notFound) {
    return <main data-testid="article-not-found" className="mx-auto max-w-3xl px-5 py-32 text-center"><h1 className="font-serif text-5xl">Article not found.</h1><Link to="/insights" className="mt-8 inline-flex border border-[#1C1613] px-6 py-4 text-xs font-bold tracking-[0.16em]">BACK TO INSIGHTS</Link></main>;
  }
  if (!article) {
    return (
      <main data-testid="article-loading" className="flex min-h-[70vh] items-center justify-center px-5 py-32">
        <p className="font-serif text-3xl italic text-[#524842]">Loading article…</p>
      </main>
    );
  }

  return <ArticleExperience article={article} onEnquiry={onEnquiry} />;
}
